import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {useHistory} from 'react-router-dom';
import {Breadcrumbs, FlexColumn, Link} from '@forecast-it/design-system';
import {TopHeaderBar} from '../../../../shared/components/headers/top-header-bar/TopHeaderBar';
import {useTrackPage} from '../../../../../tracking/amplitude/hooks/useTrackPage';
import type {CreateBaselineTemplatePageQuery$data} from './__generated__/CreateBaselineTemplatePageQuery.graphql';
import {BASELINE_TEMPLATES_PATH} from '../../templatesPaths';
import {CreateBaselineTemplateForm} from './CreateBaselineTemplateForm';
import {Option} from './baselineTemplateDraft';

export const CreateBaselineTemplatePageQuery = graphql`
	query CreateBaselineTemplatePageQuery {
		viewer {
			actualPersonId
			component(name: "settings_create_baseline_template")
			company {
				id
				currency
				roles(first: 10000, includeDisabled: false) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
				expenseCategories(first: 10000) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
				baselineTemplates {
					id
					name
				}
			}
		}
	}
`;

type Props = {viewer: CreateBaselineTemplatePageQuery$data['viewer']};

const byLabel = (a: Option, b: Option) => a.label.localeCompare(b.label);

export const CreateBaselineTemplatePage = ({viewer}: Props): JSX.Element | null => {
	useTrackPage('Create Baseline Template');
	const {formatMessage} = useIntl();
	const history = useHistory();
	const company = viewer?.company;

	const roles = useMemo<Option[]>(
		() =>
			// includeDisabled: false is not honoured by the company service, so disabled roles are dropped
			// here too, the same as categories. The server refuses a template that uses one.
			(company?.roles?.edges ?? [])
				.flatMap(edge => (edge?.node && !edge.node.disabled ? [{id: edge.node.id, label: edge.node.name ?? ''}] : []))
				.sort(byLabel),
		[company]
	);
	const categories = useMemo<Option[]>(
		() =>
			(company?.expenseCategories?.edges ?? [])
				.flatMap(edge => (edge?.node && !edge.node.disabled ? [{id: edge.node.id, label: edge.node.name ?? ''}] : []))
				.sort(byLabel),
		[company]
	);
	const existingNames = useMemo(
		() => (company?.baselineTemplates ?? []).flatMap(template => (template?.name ? [template.name] : [])),
		[company]
	);

	if (!company) {
		return null;
	}
	return (
		<FlexColumn gap="l">
			<Breadcrumbs>
				<Breadcrumbs.Item isDisabled>
					<span>{formatMessage({id: 'baseline_templates.breadcrumb.admin'})}</span>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					{/* The design-system Link carries the link colour. The click goes through the router, so
					    leaving with unsaved changes still asks first, the same as Cancel. */}
					<Link
						href={BASELINE_TEMPLATES_PATH}
						onClick={(event: React.MouseEvent) => {
							event.preventDefault();
							history.push(BASELINE_TEMPLATES_PATH);
						}}
						data-cy="baseline-template-breadcrumb-templates"
					>
						{formatMessage({id: 'settings.templates.title'})}
					</Link>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					<span>{formatMessage({id: 'baseline_templates.breadcrumb.new'})}</span>
				</Breadcrumbs.Item>
			</Breadcrumbs>
			<TopHeaderBar title={formatMessage({id: 'baseline_templates.create_template'})} />
			<CreateBaselineTemplateForm
				roles={roles}
				categories={categories}
				currency={company.currency ?? 'USD'}
				existingNames={existingNames}
			/>
		</FlexColumn>
	);
};
